import React, { useState, useEffect } from 'react';
import { Typography, Image, Space, Avatar, Card, Row, Col } from 'antd';
import { IMapPoint } from '../../types';
import { questionCardStyles } from '../../styles/questionCard';
import { IMapController } from '../../../../lib/map/types';

const { Text } = Typography;

interface MapPointDisplayProps {
  point: IMapPoint;
  index: number;
  color: string;
  markerId?: string;
  mapController?: IMapController;
}

const MapPointDisplay: React.FC<MapPointDisplayProps> = ({
  point,
  index,
  color,
  markerId,
  mapController,
}) => {
  const [isSelected, setIsSelected] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!mapController?.addMarkerEventListener || !markerId) return;

    const handleSelect = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { markerId: selectedMarkerId } = customEvent.detail;
      setIsSelected(selectedMarkerId === null ? false : selectedMarkerId === markerId);
    };

    const handleSelectionChanged = () => {
      const markerManager = mapController.getMarkerManager?.();
      if (markerManager) {
        const marker = markerManager.getMarker?.(markerId);
        if (marker) {
          setIsSelected(marker.selected || false);
        }
      }
    };

    mapController.addMarkerEventListener('marker:select', handleSelect);
    mapController.addMarkerEventListener('marker:selectionChanged', handleSelectionChanged);

    return () => {
      mapController.removeMarkerEventListener?.('marker:select', handleSelect);
      mapController.removeMarkerEventListener?.('marker:selectionChanged', handleSelectionChanged);
    };
  }, [mapController, markerId]);

  const handleClick = () => {
    if (markerId && mapController) {
      if (isSelected) {
        mapController.deselectAllMarkers?.();
      } else {
        mapController.selectMarker?.(markerId);
      }
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (markerId && mapController?.hoverMarker) {
      mapController.hoverMarker(markerId);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (mapController?.hoverMarker) {
      mapController.hoverMarker(null);
    }
  };

  return (
    <Card
      size="small"
      hoverable
      style={{
        ...questionCardStyles.mapPointCard,
        borderColor: isSelected
          ? '#4ecdc4'
          : isHovered
            ? '#91d5ff'
            : questionCardStyles.mapPointCard.borderColor,
        backgroundColor: isSelected
          ? 'rgba(78, 205, 196, 0.1)'
          : isHovered
            ? 'rgba(145, 213, 255, 0.1)'
            : questionCardStyles.mapPointCard.backgroundColor,
      }}
      styles={{
        body: questionCardStyles.mapPointCardBody,
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <Space align="start" size={10} style={questionCardStyles.mapPointSpace}>
        <Avatar size={20} style={questionCardStyles.mapPointAvatar(color)}>
          {index}
        </Avatar>

        <div style={questionCardStyles.mapPointContent}>
          <Space direction="vertical" size={4} style={questionCardStyles.mapPointContentSpace}>
            {/* <Text code style={questionCardStyles.mapPointCoordinates}>
              {point.coordinate.lat.toFixed(6)}, {point.coordinate.lng.toFixed(6)}
            </Text> */}

            {point.comment && (
              <Text style={questionCardStyles.mapPointComment}>{point.comment}</Text>
            )}

            {point.images && point.images.length > 0 && (
              <Row gutter={[4, 4]}>
                <Image.PreviewGroup>
                  {point.images.map((image, imgIndex) => (
                    <Col span={12} key={imgIndex}>
                      <Image
                        src={image.compressed || image.original}
                        alt={`Point ${index} image ${imgIndex + 1}`}
                        style={questionCardStyles.mapPointImageSquare}
                        preview={{
                          src: image.original,
                        }}
                      />
                    </Col>
                  ))}
                </Image.PreviewGroup>
              </Row>
            )}
          </Space>
        </div>
      </Space>
    </Card>
  );
};

export default MapPointDisplay;
